import { Component, OnInit, Renderer, ChangeDetectorRef, AfterViewInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { ToasterService, Toast, ToasterConfig } from 'angular2-toaster';
import { Observable, Subscription } from 'rxjs';
import { CookieService } from 'angular2-cookie/core';

import { OptionsService } from '../services/options.service';
import { ApplicationStateService } from '../services/application-state.service';
import { OptionsAdminService } from '../services/options-admin.service';
import { AdjustmentService } from '../services/adjustments.service';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { PageTitleService } from '../services/page-title.service';
import { OptionsAsideService } from '../services/options-aside.service';
import { QuoteInfoService } from '../services/quote-info.service';
import { QuoteSummaryService } from '../services/quote-summary.service';
import { AddCustomOptionService } from '../services/add-custom-option.service';
import { CurrentUserService } from '../services/current-user.service';
import { QuoteService } from '../services/quote.service';
import { ModalDialogService } from '../reusable-components/modal-dialog/modal-dialog.service';
import { BrowserWidthService } from '../services/browser-width.service';
import { HttpResponseService } from '../services/httpresponse-service';

import { Quote } from '../model/quote';
import { Option } from '../model/option';
import { SelectedOption } from '../model/selected-option';
import { Adjustment } from '../model/adjustment';
import { AdminOptionCategory } from '../model/admin-option-category';
import { Constants } from '../model/constants';

import { OptionsSearchPipe } from '../pipes/options-search.pipe';
import { PricingHeader } from '../model/pricing-header';
import { Category } from '../model/category';
import { TabHostComponent } from '../reusable-components/tab-menu/tab-host.component';

@Component({
    selector: 'my-options-configuration',
    template: require('./options-configuration.component.html'),
    styles: [require('./options-configuration.component.scss')],
})
export class OptionsConfigurationComponent implements OnInit, AfterViewInit, OnDestroy {
    categoryId: number;
    optionNumbers: number[];
    selectedQuantity;
    quoteId: number;
    currentPricingView: number;
    currentUserId: number;
    height: number;
    modelId: number;

    stateError: string = '';
    categoryName: string;
    searchTerm: string = '';
    mchBaseUrl: string;
    modifiedBy: string;

    navCategories: AdminOptionCategory[];
    mappedOptions: SelectedOption[];
    mergedOptions: Option[];
    invalidOptions: SelectedOption[];
    quote: Quote;
    customOption: SelectedOption;
    currentCategory: AdminOptionCategory;
    optionsAllCache: Option[];
    incrementQuantityOption: Option;
    quoteAdjustment: Adjustment;
    search: Observable<any>;
    config: ToasterConfig = new ToasterConfig({
        positionClass: this.browserWidthService.getDisplayMode().width.innerWidth < 769 ? 'toast-bottom-full-width' : 'toast-bottom-right',
    });
    @ViewChild('search') input: ElementRef;
    @ViewChild(TabHostComponent) tabs: TabHostComponent;
    subscription: Subscription = new Subscription();

    showInvalidSubOptions: boolean = false;
    validQuantity: boolean = true;
    facilityUser: boolean = true;
    faciltiyUserWithoutApprover: boolean = false;
    facilityUserWithApprover: boolean = false;
    stateIsInvalid: boolean = false;
    enableSave: boolean = true;
    displayNoMatches: boolean = false;
    showIncludeExcludeModal: boolean = false;
    disableCategoryDropDown: boolean = false;
    showResetModal: boolean = false;
    showTabAlerts: boolean = false;

    // added/changed for performance refactor
    allSelectedOptions: SelectedOption[] = undefined;
    selectedOptionsWithSubOptions: SelectedOption[] = undefined;
    currentSelectedOptions: SelectedOption[] = undefined;
    pricingHeader: PricingHeader = undefined;

    constructor(
        public optionsService: OptionsService,
        public route: ActivatedRoute,
        public router: Router,
        public rd: Renderer,
        public cdr: ChangeDetectorRef,
        public ref: ElementRef,
        public applicationStateService: ApplicationStateService,
        public optionsAdminService: OptionsAdminService,
        public adjustmentService: AdjustmentService,
        public toasterService: ToasterService,
        public spinner: LoadingSpinnerService,
        public pageTitleService: PageTitleService,
        public optionsAsideService: OptionsAsideService,
        public quoteInfoService: QuoteInfoService,
        public addCustomOptionService: AddCustomOptionService,
        public quoteSummaryService: QuoteSummaryService,
        public currentUserService: CurrentUserService,
        public modalDialogService: ModalDialogService,
        public optionsSearchPipe: OptionsSearchPipe,
        public cookieService: CookieService,
        public quoteService: QuoteService,
        public httpResponseService: HttpResponseService,
        public browserWidthService: BrowserWidthService
    ) {
        const routeParamsSub = this.route.params.subscribe(x => {
            this.quoteId = x['quoteId'];
            this.quoteInfoService.quoteId$.next(this.quoteId);
        });
        this.subscription.add(routeParamsSub);
        this.pageTitleService.pageTitle.next({
            title: Constants.CmhConnectionTitle,
        });
        this.mchBaseUrl = this.cookieService.get('mchBaseUrl');
        const resizeEventSub = Observable.fromEvent(window, 'resize').subscribe(() => {
            this.height = document.getElementById('content-wrapper').scrollHeight + 114;
        });
        this.subscription.add(resizeEventSub);
    }

    ngOnInit() {
        this.spinner.showSpinner();
        this.disableCategoryDropDown = false;

        const currentPricingViewSub = this.quoteInfoService.currentPricingView$.subscribe(x => {
            this.currentPricingView = x;
        });
        this.subscription.add(currentPricingViewSub);

        const modelIdSub = this.quoteInfoService.modelId$.subscribe(x => {
            this.modelId = x;
        });
        this.subscription.add(modelIdSub);

        const categoryIdSub = this.quoteInfoService.categoryId$.subscribe(x => {
            this.categoryId = +x;
        });
        this.subscription.add(categoryIdSub);

        const mappedOptionsSub = this.optionsService.mappedOptions$.subscribe(x => {
            if (x !== undefined && x.length > 0) {
                this.mappedOptions = x;
            }
        });
        this.subscription.add(mappedOptionsSub);

        const currentSelectOptionsSub = this.optionsService.currentSelectedOptions$.subscribe(x => {
            this.currentSelectedOptions = x;
        });
        this.subscription.add(currentSelectOptionsSub);

        const allSelectedOptionsSub = this.optionsService.allSelectedOptions$.subscribe(x => {
            this.allSelectedOptions = x;
            if (this.showTabAlerts) {
                this._markInvalidOptions();
            }
        });
        this.subscription.add(allSelectedOptionsSub);

        const enableSaveButtonSub = this.optionsService.enableSaveButton$.subscribe(x => {
            this.enableSave = x;
        });
        this.subscription.add(enableSaveButtonSub);

        const includeExcludeSub = this.optionsAsideService.includeExcludeModalStatus.subscribe(x => {
            this.showIncludeExcludeModal = x;
            this.browserWidthService.modalActive$.next(x);
        });
        this.subscription.add(includeExcludeSub);

        const includeExcludeOptionSub = this.optionsAsideService.includeExcludeOption.subscribe(x => {
            this.incrementQuantityOption = x;
        });
        this.subscription.add(includeExcludeOptionSub);

        if (this.quoteId === undefined || this.quoteId === 0) {
            this.spinner.hideSpinner();
            this.modalDialogService
                .showMessage({
                    headerText: 'Configure Options',
                    message: Constants.NewQuoteMessage,
                })
                .subscribe(messageReply => {
                    this.redirectRequired(true, false, false);
                });
        } else {
            const refreshStateFromDbSub = this.applicationStateService.refreshStateFromDB(this.quoteId, true).subscribe(
                dbQuote => {
                    if (dbQuote !== undefined && dbQuote !== null) {
                        this.quote = dbQuote;
                        this.pageTitleService.pageTitle.next({
                            title: Constants.CmhConnectionTitle,
                            subTitle:
                                Constants.CurrentQuoteSubTitle +
                                (dbQuote.iseriesQuoteNumber > 0 && dbQuote.QuoteStatusesID > 0 ? dbQuote.iseriesQuoteNumber : this.quoteId),
                        });
                        this.quoteInfoService.quote$.next(dbQuote);
                        const canAccessQuoteSub = this.quoteInfoService.canAccessQuote$.subscribe(canAccessQuote => {
                            if (canAccessQuote === true) {
                                if (
                                    this.quote.productLineId === undefined ||
                                    this.quote.productLineId === null ||
                                    this.quote.productLineId === 0
                                ) {
                                    this.spinner.hideSpinner();
                                    this.stateIsInvalid = true;
                                    this.modalDialogService
                                        .showMessage({
                                            headerText: Constants.NoProductLineHeader,
                                            message: Constants.NoProductLineMessage,
                                        })
                                        .subscribe(messageReply => {
                                            this.redirectRequired(false, true, false);
                                        });
                                } else {
                                    if (this.quote.modelId === undefined || this.quote.modelId === null || this.quote.modelId === 0) {
                                        this.spinner.hideSpinner();
                                        this.stateIsInvalid = true;
                                        this.modalDialogService
                                            .showMessage({
                                                headerText: Constants.NoModelHeader,
                                                message: Constants.NoModelMessage,
                                            })
                                            .subscribe(messageReply => {
                                                this.redirectRequired(false, false, true);
                                            });
                                    } else {
                                        this.quoteSummaryService.getPricingHeader(dbQuote.quoteId).subscribe(
                                            x => {
                                                this.pricingHeader = x;
                                                this.quoteAdjustment = x.AdjustmentInfo;
                                                let appState = this.applicationStateService.getApplicationState();
                                                if (appState.categoryId === undefined || appState.categoryId === null) {
                                                    this.applicationStateService.setCategoryId(
                                                        Number(sessionStorage.getItem('categoryId'))
                                                    );
                                                } else {
                                                    sessionStorage.setItem('categoryId', appState.categoryId.toString());
                                                }
                                            },
                                            error => {
                                                this.spinner.hideSpinner();
                                                this.httpResponseService.httpErrorResponse$.next(error);
                                            }
                                        );
                                        this.loadPage();
                                    }
                                }
                            } else {
                                this.spinner.hideSpinner();
                            }
                        });
                        this.subscription.add(canAccessQuoteSub);
                    } else {
                        this.spinner.hideSpinner();
                        this.stateIsInvalid = true;
                        this.modalDialogService
                            .showMessage({
                                headerText: 'Configure Options',
                                message: Constants.QuoteNotFoundMessage.replace('#quoteid#', this.quoteId.toString()),
                            })
                            .subscribe(messageReply => {
                                this.redirectRequired(true, false, false);
                            });
                    }
                },
                error => {
                    this.spinner.hideSpinner();
                    this.httpResponseService.httpErrorResponse$.next(error);
                }
            );
            this.subscription.add(refreshStateFromDbSub);
        }

        const currentUserSub = this.currentUserService.currentUser$.subscribe(cu => {
            this.currentUserId = cu.cmhUserId || cu.CmhUserId;
            this.modifiedBy = cu.cmhUserId + '-' + cu.firstName + ' ' + cu.lastName;
            if (cu.isPlantUser) {
                cu.roles.includes('MyCustomHomes_Quote_Approver') ? this.setUser(false, false, true) : this.setUser(false, true, false);
            } else {
                this.setUser(true, false, false);
            }
        });
        this.subscription.add(currentUserSub);

        const adminOptionsExistSub = this.optionsService.AdminOptionsExist$.subscribe(x => {
          
                this.getHomeCategoryDropDown();
        });
        this.subscription.add(adminOptionsExistSub);
    }

    resetSearchData() {
        this.displayNoMatches = false;
        this.search = Observable.fromEvent(this.input.nativeElement, 'keyup').debounceTime(250);
        this.input.nativeElement.value = '';
        const searchSub = this.search.subscribe(x => {
            this.searchTerm = x;
            this.optionsService.searchTerm$.next(this.searchTerm);
        });
        this.subscription.add(searchSub);
    }

    ngAfterViewInit() {
        this.resetSearchData();
    }

    ngOnDestroy() {
        sessionStorage.removeItem('categoryId');
        this.optionsService.initialOptionPageLoad$.next(true);
        this.saveSelectedOptionsOnDestroy();
        this.resetSearchData();
        this.subscription.unsubscribe();
    }

    redirectRequired(newQuotesRedirect: boolean, productLinesRedirect: boolean, modelsRedirect: boolean) {
        this.spinner.hideSpinner();
        this.stateIsInvalid = true;
        if (newQuotesRedirect) {
            this.router.navigate([Constants.NewQuotesUrl]);
        }
        if (productLinesRedirect) {
            window.location.href = `${this.mchBaseUrl}/${[Constants.ProductLineUrl.replace('#quoteid#', this.quoteId.toString())]}`;
        }
        if (modelsRedirect) {
            window.location.href = `${this.mchBaseUrl}/${[Constants.ModelUrl.replace('#quoteid#', this.quoteId.toString())]}`;
        }
    }

    /// Loads all the startup functions when the page loads
    loadPage() {
        this.resetVariables();
        this.getCategoryDropdown();       
    }

    // Resets all variables in this class
    resetVariables() {
        this.mappedOptions = new Array<SelectedOption>();
        this.invalidOptions = new Array<SelectedOption>();
        this.disableCategoryDropDown = false;
        this.customOption = new SelectedOption();
        this.navCategories = new Array<AdminOptionCategory>();
        this.currentCategory = new AdminOptionCategory();
        this.searchTerm = '';
        this.resetSearchData();
    }

    getHomeCategoryDropDown() {
        if (this.quote !== undefined && this.quote !== null) {
            const homeCategoriesConst = this.optionsAdminService.getCategoriesByModel(this.quote.modelId).subscribe(
                x => {
                    let homeCategories = new Array<Category>();
                    if (x !== undefined && x !== null) {
                        homeCategories = x;
                        this.navCategories = new Array<AdminOptionCategory>();
                        homeCategories.forEach(hc => {
                            let aoCategory = new AdminOptionCategory();
                            aoCategory.WebCategoryId = hc.CategoryId;
                            aoCategory.WebCategoryDesc = hc.CategoryDescription;
                            this.navCategories.push(aoCategory);
                        });
                        this.categoryId = Number(sessionStorage.getItem('categoryId'));
                        // this.categoryId = this.navCategories[0].WebCategoryId;
                        sessionStorage.setItem('categoryId', this.categoryId.toString());

                        this.navCategories.sort((a, b) => {
                            if (a.WebCategoryDesc < b.WebCategoryDesc) {
                                return -1;
                            }
                            if (a.WebCategoryDesc > b.WebCategoryDesc) {
                                return 1;
                            }
                            return 0;
                        });

                        if (this.navCategories.find(c => c.WebCategoryId === this.categoryId)) {
                            this.currentCategory.WebCategoryId = this.categoryId;
                            this.applicationStateService.setCategoryId(this.categoryId);
                            this.quoteInfoService.categoryId$.next(this.categoryId);
                        } else {
                            this.currentCategory.WebCategoryId = this.navCategories[0].WebCategoryId;
                            this.categoryId = this.currentCategory.WebCategoryId;
                            sessionStorage.setItem('categoryId', this.categoryId.toString());
                            this.applicationStateService.setCategoryId(this.categoryId);
                            this.quoteInfoService.categoryId$.next(this.categoryId);
                        }
                    }
                },
                error => {
                    this.spinner.hideSpinner();
                    this.httpResponseService.httpErrorResponse$.next(error);
                }
            );
            this.subscription.add(homeCategoriesConst);
        }
    }

    /// Gets the categories for the dropdown if there are multiple categories
    getCategoryDropdown() {
        if (this.quote !== undefined && this.quote !== null) {
            const optionAdminCategoryConst = this.optionsAdminService
                .getOptionsAdminCategories(this.quote.plantId, this.quote.modelId)
                .subscribe(
                    x => {
                        this.navCategories = new Array<AdminOptionCategory>();
                        if (x !== undefined && x !== null) {
                            this.navCategories = x;
                            this.categoryId = Number(sessionStorage.getItem('categoryId'));

                            this.navCategories.sort((a, b) => {
                                if (a.WebCategoryDesc < b.WebCategoryDesc) {
                                    return -1;
                                }
                                if (a.WebCategoryDesc > b.WebCategoryDesc) {
                                    return 1;
                                }
                                return 0;
                            });

                            if (this.navCategories.find(c => c.WebCategoryId === this.categoryId)) {
                                this.currentCategory.WebCategoryId = this.categoryId;
                                this.applicationStateService.setCategoryId(this.categoryId);
                                this.quoteInfoService.categoryId$.next(this.categoryId);
                            } else {
                                this.currentCategory.WebCategoryId = this.navCategories[0].WebCategoryId;
                                this.categoryId = this.currentCategory.WebCategoryId;
                                sessionStorage.setItem('categoryId', this.categoryId.toString());
                                this.applicationStateService.setCategoryId(this.categoryId);
                                this.quoteInfoService.categoryId$.next(this.categoryId);
                            }
                        }
                    },
                    error => {
                        this.spinner.hideSpinner();
                        this.httpResponseService.httpErrorResponse$.next(error);
                    }
                );
            this.subscription.add(optionAdminCategoryConst);
        }
    }

    get currentCategoryName() {
        let adminOptionCategory = new AdminOptionCategory();
        if (this.navCategories == null) {
            // nothting to do here
        } else {
            adminOptionCategory = this.navCategories.find(x => x.WebCategoryId === this.categoryId);
        }
        return adminOptionCategory == null ? '' : adminOptionCategory.WebCategoryDesc == null ? '' : adminOptionCategory.WebCategoryDesc;
    }

    getCategoryName(categoryId: number) {
        let adminOptionCategory = new AdminOptionCategory();
        if (this.navCategories == null) {
            // nothting to do here
        } else {
            adminOptionCategory = this.navCategories.find(x => x.WebCategoryId === categoryId);
        }
        return adminOptionCategory == null ? '' : adminOptionCategory.WebCategoryDesc == null ? '' : adminOptionCategory.WebCategoryDesc;
    }

    /// Saves the SelectedOptions and navigates to next page
    saveNext() {
        this.showTabAlerts = true;
        if (this._allowPageChange()) {
            this.disableCategoryDropDown = false;
            if (this.optionsService.optionsChanged$.value === true && this.mappedOptions.length > 0) {
                this.spinner.showSpinner();
                this.optionsService.initialOptionPageLoad$.next(true);
                let quote = this.applicationStateService.getQuote();
                let currentUser = this.applicationStateService.getCurrentUser();
                let totalOptions = this._calculateOptionPrice();
                let selectedOptions = this.mappedOptions.map(option => {
                    option.QuoteRevisionNumber = quote.revisionNumber;
                    return option;
                });
                this.optionsService
                    .saveSelectedOptions(
                        // this.mappedOptions,
                        selectedOptions,
                        this.categoryId,
                        this.quote.modelId,
                        this.currentPricingView,
                        this.quoteAdjustment
                    )
                    .map(x => {
                        this.toasterService.pop('success', 'Success!', 'Options have been saved');
                        this.optionsService.optionsChanged$.next(false);
                    })
                    .subscribe(
                        data => {
                            this.quoteSummaryService.getPricingHeader(this.quoteId).subscribe(
                                x => {
                                    let ph = x;
                                    ph.DuesOnly = ph.DuesOnly === undefined || ph.DuesOnly === null ? 0 : ph.DuesOnly;
                                    ph.Surcharge = ph.Surcharge === undefined || ph.Surcharge === null ? 0 : ph.Surcharge;
                                    ph.Taxes = ph.Taxes === undefined || ph.Taxes === null ? 0 : ph.Taxes;
                                    ph.Transportation =
                                        ph.Transportation === undefined || ph.Transportation === null ? 0 : ph.Transportation;
                                    this.applicationStateService.setQuotePricingAndUpdate(
                                        ph.BaseCost,
                                        ph.ModelBaseOverride,
                                        ph.DuesOnly,
                                        totalOptions,
                                        ph.VIPDiscountAmount,
                                        ph.PartnerAssistanceAmount,
                                        ph.Surcharge,
                                        ph.Taxes,
                                        ph.Transportation,
                                        ph.GrandTotal
                                    );
                                },
                                error => {
                                    this.spinner.hideSpinner();
                                    this.httpResponseService.httpErrorResponse$.next(error);
                                }
                            );
                        },
                        error => {
                            this.spinner.hideSpinner();
                            this.httpResponseService.httpErrorResponse$.next(error);
                        },
                        () => {
                            this.spinner.hideSpinner();
                            this.router.navigate(['order-information', this.quoteId]);
                        }
                    );
            } else {
                this.optionsService.optionsChanged$.next(false);
                this.router.navigate(['order-information', this.quoteId]);
            }
        } else {
            this._markInvalidOptions();
            let allowNavigate = true;
            let badOptions = new Array<SelectedOption>();
            badOptions = this._allBadOptionsList;

            if (badOptions.length > 0) {
                allowNavigate = false;
            }

            if (!allowNavigate) {
                this.displayModal(badOptions);
            }
        }
    }

    /// Saves all the SelectedOptions to the db -- ONLY CALL THIS FROM NGONDESTROY
    saveSelectedOptionsOnDestroy() {
        if (this.optionsService.optionsChanged$.value === true && this.mappedOptions.length > 0) {
            let quote = this.applicationStateService.getQuote();
            let currentUser = this.applicationStateService.getCurrentUser();
            let totalOptions = this._calculateOptionPrice();
            let selectedOptions = this.mappedOptions.map(option => {
                option.QuoteRevisionNumber = quote.revisionNumber;
                return option;
            });

            this.optionsService
                .saveSelectedOptions(
                    //  this.mappedOptions,
                    selectedOptions,
                    this.categoryId,
                    this.quote.modelId,
                    this.currentPricingView,
                    this.quoteAdjustment
                )
                .subscribe(
                    data => {
                        this.pricingHeader.DuesOnly =
                            this.pricingHeader.DuesOnly === undefined || this.pricingHeader.DuesOnly === null
                                ? 0
                                : this.pricingHeader.DuesOnly;
                        this.pricingHeader.Surcharge =
                            this.pricingHeader.Surcharge === undefined || this.pricingHeader.Surcharge === null
                                ? 0
                                : this.pricingHeader.Surcharge;
                        this.pricingHeader.Taxes =
                            this.pricingHeader.Taxes === undefined || this.pricingHeader.Taxes === null ? 0 : this.pricingHeader.Taxes;
                        this.pricingHeader.Transportation =
                            this.pricingHeader.Transportation === undefined || this.pricingHeader.Transportation === null
                                ? 0
                                : this.pricingHeader.Transportation;
                        this.applicationStateService.setQuotePricingAndUpdate(
                            this.pricingHeader.BaseCost,
                            this.pricingHeader.ModelBaseOverride,
                            this.pricingHeader.DuesOnly,
                            totalOptions,
                            this.pricingHeader.VIPDiscountAmount,
                            this.pricingHeader.PartnerAssistanceAmount,
                            this.pricingHeader.Surcharge,
                            this.pricingHeader.Taxes,
                            this.pricingHeader.Transportation,
                            this.pricingHeader.GrandTotal
                        );
                    },
                    error => {
                        this.spinner.hideSpinner();
                        this.httpResponseService.httpErrorResponse$.next(error);
                    },
                    () => {
                        this.optionsService.optionsChanged$.next(false);
                    }
                );
        } else {
            this.optionsService.optionsChanged$.next(false);
        }
    }

    /// Opens the AddCustomOption Aside
    viewAdd() {
        this.addCustomOptionService.showAddCustomOption$.next(true);
    }

    // invoked when category tab is selected
    changeCategory(e?): string {
        let initialCategoryId = this.categoryId;
        let pageChange: boolean = true;
        let targetCategoryId = this.categoryId;
        pageChange = this._allowPageChange(initialCategoryId);
        if (pageChange) {
            this.input.nativeElement.value = '';
            if (e) {
                targetCategoryId = this.navCategories.find(x => x.WebCategoryDesc === e).WebCategoryId;
            }

            this.disableCategoryDropDown = false;
            this._saveOptionsAndChangeCategory(initialCategoryId, targetCategoryId);
            return this.getCategoryName(targetCategoryId) == null ? '' : this.getCategoryName(targetCategoryId);
        } else {
            this.spinner.hideSpinner();
            this.categoryId = initialCategoryId;
            this.tabs.revertNavigation();
            return this.currentCategoryName == null ? '' : this.currentCategoryName;
        }
    }

    /// Sets the current user based on which permission button was selected
    setUser(notFacility: boolean, withoutApprover: boolean, withApprover: boolean) {
        this.facilityUser = notFacility;
        this.faciltiyUserWithoutApprover = withoutApprover;
        this.facilityUserWithApprover = withApprover;
    }

    displayModal(options: SelectedOption[]) {
        this.height = this.height = document.getElementById('content-wrapper').scrollHeight + 114;
        let errorMessage = '';
        if (this.invalidOptions === undefined) {
            this.invalidOptions = new Array<SelectedOption>();
        }
        options.forEach(x => {
            this.invalidOptions.push(x);

            this.showInvalidSubOptions = true;
            this.browserWidthService.modalActive$.next(true);
        });
        this.invalidOptions.sort((x, y) => (x.CategoryName < y.CategoryName ? -1 : 1));

        this.showInvalidSubOptions = true;
        this.browserWidthService.modalActive$.next(true);
    }

    closeInvalidOptionModal() {
        this.showInvalidSubOptions = false;
        this.browserWidthService.modalActive$.next(false);
        this.invalidOptions = [];
    }

    canDeactivate(): boolean {
        let allowNavigate = true;

        if (this.quoteInfoService.canAccessQuote$.value === true && !this.stateIsInvalid) {
            if (this._allBadOptionsList !== undefined && this._allBadOptionsList !== null && this._allBadOptionsList.length > 0) {
                allowNavigate = false;
                if (!this.showInvalidSubOptions) {
                    this.optionsService.checkSubOptions$.next(true);
                    this.displayModal(this._allBadOptionsList);
                }
            }

            if (!this.enableSave && allowNavigate) {
                allowNavigate = false;
                this.modalDialogService
                    .showMessage({
                        headerText: 'Options-Configuration',
                        message: Constants.ValidationErrorsMessage,
                    })
                    .subscribe(messageReply => {});
            }
        }

        return allowNavigate;
    }

    /// returns boolean indicator if invalid suboptions exist
    get _subOptionsValid(): boolean {
        return this.allSelectedOptions.find(o => o.SubOptionCount > 0 && o.InvalidSubOption) === undefined;
    }

    /// this is a function because it is used multiple places and I did not want to duplicate code
    get _allBadOptionsList(): SelectedOption[] {
        let badOptions = new Array<SelectedOption>();

        if (badOptions.length === 0) {
            if (this.allSelectedOptions !== undefined && this.allSelectedOptions.length > 0) {
                this.allSelectedOptions
                    .filter(x => x.SubOptionCount > 0 && x.SelectedSubOptionId <= 0 && x.Visible && x.Quantity > 0)
                    .forEach(option => {
                        badOptions.push(option);
                    });
            }
        }

        return badOptions;
    }

    isTabInvalid(categoryId) {
        if (this.showTabAlerts) {
            return this.allSelectedOptions.find(o =>
                o.SubOptionCount > 0
                && o.Quantity > 0
                && o.HomeOptionsSubOptionId <= 0
                && o.Visible
                && o.HomeOptionsCategoryId == categoryId) !== undefined;
        }
        return false;
    }

    _markInvalidOptions() {
        if (this.allSelectedOptions !== undefined && this.allSelectedOptions.length > 0) {
            this.allSelectedOptions
                .filter(aso => aso.SubOptionCount > 0 && aso.Quantity > 0 && aso.Visible)
                .forEach(sco => {
                    sco.InvalidSubOption = sco.SelectedSubOptionId > 0 ? false : true;
                });
        }
    }

    get spinnerControl(): Array<string> {
        return;
    }

    incrementQuantityFromModal() {
        this.optionsService.optionsChanged$.next(true);

        let masterIndex = this.allSelectedOptions.findIndex(mo => mo.HomeOptionsOptionId === this.incrementQuantityOption.OptionId);
        if (masterIndex > 0) {
            this.allSelectedOptions[masterIndex].WasChanged = true;
            if (this.allSelectedOptions[masterIndex].IncludeExclude && this.allSelectedOptions[masterIndex].IncludeExclude.length > 0) {
                if (this.allSelectedOptions[masterIndex].Quantity === 0) {
                    this.showIncludeExcludeModal = true;
                    this.browserWidthService.modalActive$.next(true);
                }
                if (this.showIncludeExcludeModal) {
                    this.allSelectedOptions[masterIndex].Quantity = 1;
                    this.allSelectedOptions[masterIndex].AdjustedTotalPrice =
                        this.allSelectedOptions[masterIndex].Quantity * Number(this.allSelectedOptions[masterIndex].UIUnitPrice);

                    this.allSelectedOptions[masterIndex].IncludeExclude.forEach(ieListitem => {
                        let ieIndex = this.allSelectedOptions.findIndex(aso => aso.HomeOptionsOptionId === ieListitem.OptionId);
                        if (ieIndex > 0) {
                            this.allSelectedOptions[ieIndex].WasChanged = true;
                            this.allSelectedOptions[ieIndex].IsIncluded = ieListitem.IncludedOption;
                            this.allSelectedOptions[ieIndex].IsExcluded = !ieListitem.IncludedOption;
                            this.allSelectedOptions[ieIndex].Quantity = ieListitem.OptionQuantity;
                            this.allSelectedOptions[ieIndex].UIUnitPrice = !ieListitem.IncludeOptionPrice
                                ? '0.00'
                                : this.allSelectedOptions[ieIndex].UnitPrice.toFixed(2);
                            this.allSelectedOptions[ieIndex].AdjustedTotalPrice = !ieListitem.IncludeOptionPrice
                                ? 0
                                : this.allSelectedOptions[ieIndex].Quantity * Number(this.allSelectedOptions[ieIndex].UIUnitPrice);
                        }
                    });
                } else {
                    if (Number(this.allSelectedOptions[masterIndex].UIUnitPrice) > 0) {
                        this.allSelectedOptions[masterIndex].Quantity =
                            this.allSelectedOptions[masterIndex].Quantity + 1 >= 9999
                                ? 9999
                                : this.allSelectedOptions[masterIndex].Quantity + 1;
                        this.allSelectedOptions[masterIndex].AdjustedTotalPrice =
                            this.allSelectedOptions[masterIndex].Quantity * Number(this.allSelectedOptions[masterIndex].UIUnitPrice);
                    } else {
                        this.allSelectedOptions[masterIndex].Quantity = 1;
                        this.allSelectedOptions[masterIndex].AdjustedTotalPrice =
                            this.allSelectedOptions[masterIndex].Quantity * Number(this.allSelectedOptions[masterIndex].UIUnitPrice);
                    }
                }
            }
        }
        this.optionsService.includeExcludeAllSelectedOptions$.next(this.allSelectedOptions);
        this.showIncludeExcludeModal = false;
        this.browserWidthService.modalActive$.next(false);
        this.optionsAsideService.includeExcludeModalStatus.next(false);
    }

    closeIncludeExcludeModal() {
        this.showIncludeExcludeModal = false;
        this.browserWidthService.modalActive$.next(false);
        this.optionsAsideService.includeExcludeModalStatus.next(false);
    }

    openResetModal() {
        this.height = this.height = document.getElementById('content-wrapper').scrollHeight + 114;
        this.showResetModal = true;
        this.browserWidthService.modalActive$.next(true);
    }

    closeResetModal(reset: boolean) {
        this.showResetModal = false;
        this.optionsService.resetSelectedOptions$.next(reset);
        this.browserWidthService.modalActive$.next(false);
    }

    filterEmptyCategories(categories: Array<AdminOptionCategory>, selectedOptions: SelectedOption[]): Array<AdminOptionCategory> {
        if (!categories || !selectedOptions) {
            return categories;
        }

        let filteredCategories =  categories.filter(category =>
            selectedOptions.find(option => option.HomeOptionsCategoryId === category.WebCategoryId && option.Visible)
        );

        return filteredCategories.length ? filteredCategories : categories;
    }

    private _allowPageChange(categoryId?: number) {
        let allowPageChange = true;
        let toastAlreadyShown = false;
        this.allSelectedOptions =
            this.allSelectedOptions === undefined || this.allSelectedOptions === null
                ? new Array<SelectedOption>()
                : this.allSelectedOptions;
        categoryId = categoryId === null || categoryId === undefined ? undefined : categoryId;

        if (categoryId > 0) {
            toastAlreadyShown =
                this.allSelectedOptions.filter(x => x.HomeOptionsCategoryId === +categoryId && x.InvalidSubOption === true).length > 0
                    ? true
                    : false;
            this.allSelectedOptions
                .filter(
                    x =>
                        x.HomeOptionsCategoryId === +categoryId &&
                        x.SubOptionCount > 0 &&
                        x.Quantity > 0 &&
                        x.SelectedSubOptionId <= 0 &&
                        x.Visible
                )
                .forEach(aso => {
                    aso.InvalidSubOption = true;
                });
            allowPageChange =
                this.allSelectedOptions.filter(
                    x =>
                        x.HomeOptionsCategoryId === +categoryId &&
                        x.SubOptionCount > 0 &&
                        x.Quantity > 0 &&
                        x.SelectedSubOptionId <= 0 &&
                        x.Visible
                ).length > 0
                    ? false
                    : true;
        } else {
            toastAlreadyShown = this.allSelectedOptions.filter(x => x.InvalidSubOption === true).length > 0 ? true : false;
            this.allSelectedOptions
                .filter(x => x.SubOptionCount > 0 && x.Quantity > 0 && x.SelectedSubOptionId <= 0 && x.Visible)
                .forEach(aso => {
                    aso.InvalidSubOption = true;
                });
            allowPageChange =
                this.allSelectedOptions.filter(x => x.SubOptionCount > 0 && x.Quantity > 0 && x.SelectedSubOptionId <= 0 && x.Visible)
                    .length > 0
                    ? false
                    : true;
        }

        if (!allowPageChange && !toastAlreadyShown) {
            let toast: Toast = {
                type: 'warning',
                title: 'Invalid Options',
                body: 'There are options with invalid sub-options selected',
                onHideCallback: () => {},
            };
            this.toasterService.pop(toast);
        }

        return allowPageChange;
    }

    private _setCategoryDropDown(categoryId: number) {
        if (this.navCategories.find(c => c.WebCategoryId === categoryId)) {
            this.currentCategory.WebCategoryId = categoryId;
            this.categoryId = this.currentCategory.WebCategoryId;
        } else {
            this.currentCategory.WebCategoryId = this.navCategories[0].WebCategoryId;
            this.categoryId = this.currentCategory.WebCategoryId;
        }
        sessionStorage.setItem('categoryId', this.categoryId.toString());
        this.applicationStateService.setCategoryId(categoryId);
        this.quoteInfoService.categoryId$.next(categoryId);
    }

    private _saveOptionsAndChangeCategory(initialCategoryId: number, targetCategoryId: number) {
        if (this.optionsService.optionsChanged$.value === true) {
            this.spinner.showSpinner();
            const mappedOptionsSub = this.optionsService.mappedOptions$
                .asObservable()
                .take(1)
                .subscribe(x => {
                    this.optionsService
                        .saveSelectedOptions(x, initialCategoryId, this.quote.modelId, this.currentPricingView, this.quoteAdjustment)
                        .subscribe(
                            () => {
                                this.optionsService.initialOptionPageLoad$.next(true);
                                let toast: Toast = {
                                    type: 'success',
                                    title: 'Success',
                                    body: 'Options have been saved',
                                    onHideCallback: () => {},
                                };
                                this.toasterService.pop(toast);
                                this.optionsService.optionsChanged$.next(false);                                
                                this.quoteSummaryService.getPricingHeader(this.quoteId).subscribe(
                                    data => {
                                        this.spinner.hideSpinner();
                                        sessionStorage.setItem('categoryId', targetCategoryId.toString());
                                        this.applicationStateService.setCategoryId(targetCategoryId);
                                        this._setCategoryDropDown(targetCategoryId);
                                    },
                                    error => {
                                        this.spinner.hideSpinner();
                                        this.httpResponseService.httpErrorResponse$.next(error);
                                    }
                                );                                
                            },
                            error => {
                                this.spinner.hideSpinner();
                                this.httpResponseService.httpErrorResponse$.next(error);
                            },
                            () => {
                                this.spinner.hideSpinner();
                                sessionStorage.setItem('categoryId', targetCategoryId.toString());
                                this.applicationStateService.setCategoryId(targetCategoryId);
                                this._setCategoryDropDown(targetCategoryId);
                            }
                        );
                });
            this.subscription.add(mappedOptionsSub);
        } else {
            this.spinner.hideSpinner();

            sessionStorage.setItem('categoryId', targetCategoryId.toString());
            this.applicationStateService.setCategoryId(targetCategoryId);
            this._setCategoryDropDown(targetCategoryId);
        }
    }

    private _calculateOptionPrice(): number {
        let totalOptions = 0;
        if (this.allSelectedOptions !== undefined && this.allSelectedOptions !== null && this.allSelectedOptions.length > 0) {
            this.allSelectedOptions.forEach(o => {
                let unitPrice = 0;
                if (o.UnitPriceOverride > 0) {
                    unitPrice = o.UnitPriceOverride * o.Quantity;
                } else {
                    unitPrice = Number(o.UIUnitPrice) * o.Quantity;
                }
                totalOptions += unitPrice;
            });
        }

        return totalOptions;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/options-configuration/options-configuration.component.ts